import React from "react";
import { formatNumbersWithDecimal } from "../../components/formatNumbersWIthDecimal";

function ProductViewModal({ item, productDetails, setProductDetails, currency }) {
  if (!productDetails || !item) return null;

  // Status badge color (pill)
  const getStatusColor = (status) => {
    switch ((status || "").toUpperCase()) {
      case "IN STOCK":
        return "bg-green-100 text-green-700 border-green-300";
      case "OUT OF STOCK":
        return "bg-red-100 text-red-700 border-red-300";
      default:
        return "bg-gray-200 text-gray-700 border-gray-300";
    }
  };

  // Date formatting utility
  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const d = new Date(dateStr);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white shadow-2xl rounded-2xl w-full max-w-3xl mx-2 max-h-[90vh] overflow-y-auto border border-gray-200 relative animate-slideUp">

        {/* Close Button */}
        <button
          onClick={() => setProductDetails(false)}
          className="absolute top-5 right-5 text-3xl text-gray-400 hover:text-red-500 transition-all"
          title="Close"
          aria-label="Close"
        >
          &times;
        </button>

        {/* Modal Header */}
        <div className="flex flex-col sm:flex-row items-center sm:items-end px-8 pt-8 pb-5 gap-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 via-white to-blue-50 rounded-t-2xl">
          <div className="flex-1">
            <span className="flex gap-2 items-center text-blue-600 font-semibold uppercase tracking-wider text-xs">
              <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="4" y="4" width="16" height="16" rx="2" fill="#2563eb" /><path d="M7 10.5V9a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v1.5" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/><circle cx="9" cy="15" r="1" fill="#fff"/><circle cx="15" cy="15" r="1" fill="#fff"/></svg>
              Product:
              <span className="text-gray-900">{item.product_name}</span>
            </span>
            <h2 className="text-2xl font-extrabold text-gray-800 tracking-tight mt-1">Product Details</h2>
          </div>
          <div className="flex flex-row gap-2 flex-wrap items-center">
            <span className={`px-3 py-1 rounded-full border text-xs font-medium shadow-sm ${getStatusColor(item.status)}`}>
              {item.status || "N/A"}
            </span>
            <span className="text-gray-500 text-xs font-mono">
              {item?.sku ? <><span className="font-semibold">SKU:</span> {item.sku}</> : <span className="italic">No SKU</span>}
            </span>
            <span className="text-gray-500 text-xs font-mono">
              {item?.barcode ? <><span className="font-semibold">Barcode:</span> {item.barcode}</> : <span className="italic">No Barcode</span>}
            </span>
          </div>
        </div>

        {/* Product Profile Layout */}
        <div className="flex flex-col md:flex-row gap-7 px-8 pt-8 bg-gray-50 rounded-b-2xl">
          <div className="self-start flex flex-col items-center md:items-start min-w-[160px]">
            <div className="mb-4">
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.product_name}
                  className="rounded-xl w-40 h-40 object-cover border border-gray-200 shadow-md bg-white"
                />
              ) : (
                <div className="w-40 h-40 rounded-xl flex items-center justify-center bg-blue-100 text-5xl font-bold text-blue-500 border border-blue-200 shadow-inner select-none">
                  {item.product_name?.[0] || "?"}
                </div>
              )}
            </div>
            <div className="w-full flex flex-col gap-2">
              <div className="text-sm text-gray-500 flex gap-1 items-center">
                <span className="font-semibold text-blue-800">Price:</span>
                <span className="font-bold text-blue-700">{currency}{formatNumbersWithDecimal(item?.price)}</span>
              </div>
              <div className="text-sm text-gray-500 flex gap-1 items-center">
                <span className="font-semibold text-blue-800">Available:</span>
                <span className="text-gray-800 font-medium">{item?.available_quantity === null ? "Unlimited" : item?.available_quantity}</span>
              </div>
            </div>
          </div>
          <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-7 pb-6">
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-base font-semibold text-blue-900 mb-1">General Info</h3>
                <dl className="space-y-1">
                  <dt className="inline font-medium text-gray-600">Store:</dt>{" "}
                  <dd className="inline ml-1">{item?.store ?? <span className="italic text-gray-400">N/A</span>}</dd>
                  <br />
                  <dt className="inline font-medium text-gray-600">Warehouse:</dt>{" "}
                  <dd className="inline ml-1">{item?.warehouse ?? <span className="italic text-gray-400">N/A</span>}</dd>
                  <br />
                  <dt className="inline font-medium text-gray-600">Supplier:</dt>{" "}
                  <dd className="inline ml-1">{item?.supplier ?? <span className="italic text-gray-400">N/A</span>}</dd>
                </dl>
              </div>
              <div className="flex gap-2">
                <div className="w-1/2">
                  <div className="font-medium text-gray-600">Category</div>
                  <div className="text-gray-700 text-sm">
                    {item?.category?.cat_name ?? <span className="italic text-gray-400">N/A</span>}
                  </div>
                </div>
                <div className="w-1/2">
                  <div className="font-medium text-gray-600">Subcategory</div>
                  <div className="text-gray-700 text-sm">
                    {item?.subcategory?.sub_cat_name ?? <span className="italic text-gray-400">N/A</span>}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-5">
              <div>
                <h3 className="text-base font-semibold text-blue-900 mb-1">Dates</h3>
                <dl className="space-y-1">
                  <dt className="inline font-medium text-gray-600">Manufacturing:</dt>{" "}
                  <dd className="inline ml-1">{formatDate(item?.manufacturing_date)}</dd>
                  <br />
                  <dt className="inline font-medium text-gray-600">Expiring:</dt>{" "}
                  <dd className="inline ml-1">{formatDate(item?.expiring_date)}</dd>
                </dl>
              </div>
              <div>
                <h3 className="text-base font-semibold text-blue-900 mb-1">Description</h3>
                <div className="text-sm bg-gray-100 rounded-lg min-h-[44px] p-3 border border-gray-200 text-gray-700">
                  {item?.description ? (
                    <span>{item.description}</span>
                  ) : (
                    <span className="italic text-gray-400">No description provided.</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end items-center px-8 pb-7 pt-4 gap-4 border-t border-gray-100 bg-white rounded-b-2xl">
          <button
            autoFocus
            onClick={() => setProductDetails(false)}
            className="text-sm font-semibold bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-300 focus:outline-none text-white px-7 py-2.5 rounded-full shadow transition"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default ProductViewModal;
